<template>
  <div class="pb-5 sm:flex sm:items-center sm:justify-between">
    <h2 class="text-3xl text-vice-teal-900 font-semibold leading-tight">Dashboard</h2>
    <SystemCheckModal
      @togglePlatformRequirementsModal="togglePlatformRequirementsModal"
      v-if="isCheckPlatformRequirements"
      :is-clinician="true"
    ></SystemCheckModal>
  </div>
  <div>
    <div class="sm:hidden">
      <label for="tabs" class="sr-only">Select a tab</label>
      <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
      <select
        id="tabs"
        name="tabs"
        class="
          block
          w-full
          focus:ring-vice-teal-500
          focus:border-vice-teal-500
          border-gray-300
          rounded-md
        "
      >
        <option
          v-for="tab in tabs"
          :key="tab.name"
          :selected="tab.current"
          @click="setTabState(tab.id)"
        >
          {{ tab.name }}
        </option>
      </select>
    </div>
    <div class="hidden sm:block">
      <nav class="relative z-0 rounded-lg shadow flex divide-x divide-gray-200" aria-label="Tabs">
        <button
          v-for="(tab, tabIdx) in tabs"
          :key="tab.name"
          @click="setTabState(tab.id)"
          :class="[
            tab.current ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700',
            {
              'rounded-l-lg step-dashboard-overview': tabIdx === 0,
              'rounded-r-lg': tabIdx === tabs.length - 1,
              'step-past-sessions': tabIdx === 1,
            },
          ]"
          :id="`tab-${tab.id}`"
          class="
            group
            relative
            min-w-0
            flex-1
            overflow-hidden
            bg-white
            p-4
            text-sm
            font-medium
            text-center
            hover:bg-gray-50
            focus:z-10
          "
          :aria-current="tab.current ? 'page' : undefined"
        >
          <span>{{ tab.name }}</span>
          <span
            aria-hidden="true"
            :class="[
              tab.current ? 'bg-vice-teal-500' : 'bg-transparent',
              'absolute inset-x-0 bottom-0 h-0.5',
            ]"
          />
        </button>
      </nav>
    </div>
  </div>
  <div class="grid grid-cols-2 gap-14 pt-11">
    <!-- Tab 1: Clinician sessions block -->
    <div class="col-span-2 sm:col-span-1" :class="this.tabs[0].current ? 'block' : 'hidden'">
      <div class="relative flex items-center h-10">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
          <div class="w-full border-t border-gray-300" />
        </div>
        <div class="relative flex justify-start pb-px">
          <span class="pr-3 pb-px bg-light-grey text-lg font-medium text-vice-teal-700">
            {{ this.myClinicianSessionLabel() }}
          </span>
        </div>
      </div>

      <div class="pt-8">
        <div
          data-cy="create-session"
          @click="createNewSession"
          method="get"
          as="button"
          type="button"
          class="
            step-create-session
            relative
            bg-white
            block
            w-full
            border-2 border-gray-300 border-dashed
            rounded-lg
            p-12
            text-center
            hover:border-gray-400
            focus:outline-none
            focus:ring-2 focus:ring-offset-2 focus:ring-vice-teal-500
          "
          :class="{ 'pointer-events-none': creatingSession }"
        >
          <template v-if="creatingSession">
            <ViceButton class="w-11" red circle>
              <ViceLoader class="left-2" />
            </ViceButton>

            <span class="mt-2 block text-sm font-medium text-gray-900">
              A new session is being prepared
            </span>
          </template>
          <template v-else>
            <ViceButton icon="PlusIcon" red circle></ViceButton>
            <span class="mt-2 block text-sm font-medium text-gray-900">Create a new session</span>
          </template>
        </div>
      </div>
      <CardClinicianSession
        v-for="sessionDetails in this.$page.props.sessions.my_sessions"
        :key="sessionDetails.id"
        :sessionDetails="sessionDetails"
      ></CardClinicianSession>
    </div>
    <!-- Tab 1: Recent past block -->
    <div class="col-span-2 sm:col-span-1" :class="this.tabs[0].current ? 'block' : 'hidden'">
      <div class="relative h-10">
        <div class="absolute inset-0 flex items-center h-full" aria-hidden="true">
          <div class="w-full border-t border-gray-300" />
        </div>
        <div class="relative flex justify-between items-center h-full">
          <span class="pr-3 pb-px bg-light-grey text-lg font-medium text-vice-teal-700">
            Recent past sessions
          </span>
          <div class="flex bg-light-grey pl-3.5">
            <button
              data-cy="open-past-session-table"
              @click="setTabState('past_sessions')"
              type="button"
              class="
                flex
                pl-4
                pr-3.5
                py-2
                text-sm
                bg-white
                shadow-sm
                border border-gray-300
                focus:outline-none
                focus:ring-gray-500
                focus:border-gray-500
                sm:text-sm
                rounded-md
              "
            >
              <span class="inline-block text-gray-700">See all</span>
              <ChevronRightIcon class="inline-block text-gray-500 w-5 h-5 text-sm" />
            </button>
          </div>
        </div>
      </div>
      <CardClinicianSession
        v-for="pastSessionDetails in this.$page.props.sessions.past_sessions.slice(0, 3)"
        :key="pastSessionDetails.id"
        :sessionDetails="pastSessionDetails"
        :isPastSession="true"
        @deleteSession="openDeleteSessionDialog(pastSessionDetails.unique_id)"
      ></CardClinicianSession>

      <div
        class="mt-8 text-gray-500 text-center"
        v-if="!Object.keys($page.props.sessions.past_sessions).length"
      >
        Your past sessions will appear here.
      </div>
    </div>
    <!-- Tab 1: Upcoming block -->
    <div class="col-span-2 sm:col-span-2" :class="this.tabs[0].current ? 'block' : 'hidden'">
      <UpcomingSessions :class="this.tabs[0]['current'] ? 'block' : 'hidden'" />
    </div>

    <!-- Tab 2: Past sessions block -->
    <PastSessions :class="this.tabs[1].current ? 'block' : 'hidden'" />

    <!-- Tab 3: WiFi-Gen || Accounts block -->
    <div class="col-span-2" :class="this.tabs[2].current ? 'block' : 'hidden'">
      <div v-if="this.$page.props.user.role === clinicianRole.admin">
        <Students :class="this.tabs[2].current ? 'block' : 'hidden'" />
      </div>
      <div v-else>
        <div class="relative">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-300" />
          </div>
          <div class="relative flex justify-start">
            <span class="pr-3 bg-light-grey text-lg font-medium text-vice-teal-700">
              WiFi QR generator
            </span>
          </div>
        </div>
        <div class="w-full pt-8 flex justify-center">
          <div class="w-9/12">
            <WifiConfigurationWizard />
          </div>
        </div>
      </div>
    </div>
  </div>

  <TransitionRoot as="template" :show="deleteDialog.open">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" @close="deleteDialog.open = false">
      <div
        class="
          flex
          items-end
          justify-center
          min-h-screen
          pt-4
          px-4
          pb-20
          text-center
          sm:block
          sm:p-0
        "
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              inline-block
              align-bottom
              bg-white
              rounded-lg
              px-4
              pt-5
              pb-4
              text-left
              overflow-hidden
              shadow-xl
              transform
              transition-all
              sm:my-8
              sm:align-middle
              sm:max-w-lg
              sm:w-full
              sm:p-6
            "
          >
            <div class="p-2">
              <h3 class="text-lg leading-6 font-medium text-gray-900">Delete your session</h3>
              <div class="mt-2 max-w-xl text-sm text-gray-500">
                <p>Once you delete your session, you will lose all data associated with it!</p>
              </div>
              <div class="mt-5">
                <button
                  data-cy="confirm-delete-session"
                  @click="deletedSession"
                  v-if="!deleteDialog.waiting"
                  type="button"
                  class="
                    inline-flex
                    items-center
                    justify-center
                    px-4
                    py-2
                    border border-transparent
                    font-medium
                    rounded-md
                    text-red-700
                    bg-red-100
                    hover:bg-red-200
                    focus:outline-none
                    focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                    sm:text-sm
                  "
                >
                  Delete session
                </button>
                <button
                  v-else
                  disabled
                  type="button"
                  class="
                    inline-flex
                    items-center
                    justify-center
                    px-4
                    py-2
                    border border-transparent
                    font-medium
                    rounded-md
                    text-dark-grey
                    bg-medium-grey
                    sm:text-sm
                  "
                >
                  Deleting session...
                </button>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ChevronRightIcon } from '@heroicons/vue/outline';
import clinicianRole from '@/Composables/Enums/clinicianRole';
import CardClinicianSession from '@/Components/Cards/Session/CardClinicianSession.vue';
import DateFormatMixin from '@/Mixins/DateFormatMixin.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import PastSessions from './Session/PastSessions.vue';
import WifiConfigurationWizard from '@/Components/Devices/WifiConfigurationWizard.vue';
import SystemCheckModal from '@/Components/Modals/SystemCheckModal.vue';
import Students from '@/Pages/Clinician/Admin/Students.vue';
import UpcomingSessions from '@/Pages/User/UpcomingSessions.vue';

export default {
  components: {
    UpcomingSessions,
    Students,
    WifiConfigurationWizard,
    CardClinicianSession,
    ViceButton,
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    ViceLoader,
    PastSessions,
    ChevronRightIcon,
    SystemCheckModal,
  },
  mounted() {
    navigator.mediaDevices.ondevicechange = () => {
      this.togglePlatformRequirementsModal();
    };
  },
  beforeUnmount() {
    navigator.mediaDevices.ondevicechange = null;
  },
  setup() {
    return {
      clinicianRole,
    };
  },
  mixins: [DateFormatMixin],
  data() {
    const dynamicTab = () => ({
      id: this.$page.props.user.role === clinicianRole.admin ? 'accounts' : 'wifi_qr_generator',
      name: this.$page.props.user.role === clinicianRole.admin ? 'Accounts' : 'WiFi QR generator',
      current: false,
    });

    return {
      creatingSession: false,
      tabs: [
        { id: 'overview', name: 'Overview', current: true },
        { id: 'past_sessions', name: 'Past sessions', current: false },
        dynamicTab(),
      ],
      deleteDialog: {
        open: false,
        selected_uuid: null,
        waiting: false,
      },
      isCheckPlatformRequirements: false,
    };
  },
  methods: {
    createNewSession() {
      this.creatingSession = true;
      localStorage.setItem(`_vice:clinicianDashboardTour_tour_skipped`, true);
      setTimeout(() => {
        window.location.href = route('create.session');
      }, 0);
    },
    setTabState(id) {
      this.tabs.forEach((tab) => {
        tab.current = tab.id === id;
      });
    },
    myClinicianSessionLabel() {
      return this.$page.props.user.role === clinicianRole.admin
        ? 'Clinician sessions'
        : 'My clinical sessions';
    },
    openDeleteSessionDialog(sessionUuid) {
      this.deleteDialog.open = true;
      this.deleteDialog.selected_uuid = sessionUuid;
    },
    deletedSession() {
      this.deleteDialog.waiting = true;

      axios.post(route('delete.session', this.deleteDialog.selected_uuid)).then(() => {
        this.$page.props.sessions.past_sessions = this.$page.props.sessions.past_sessions.filter(
          (session) => session.unique_id !== this.deleteDialog.selected_uuid,
        );
        this.deleteDialog.open = false;
        this.deleteDialog.waiting = false;
      });
    },
    togglePlatformRequirementsModal(show = false) {
      this.isCheckPlatformRequirements = show;
    },
  },
};
</script>
